// v17 Đợt 1 — App-level Chunk Error Boundary.
//
// Deploy mới đổi hash chunk → client đang mở app tải lazy route cũ → 404
// ChunkLoadError → trước đây TRẮNG TRANG. Boundary này:
//   1. Lần đầu gặp ChunkLoadError → tự reload trang MỘT lần (sessionStorage
//      guard chống loop) — người dùng gần như không nhận ra.
//   2. Vẫn lỗi sau reload → hiện card "Tải lại" thay vì trắng trang.
// Lỗi runtime KHÁC (không phải chunk) → không reload, hiện card + để Sentry
// bắt qua rethrow trong componentDidCatch của Sentry boundary bên ngoài.
import React from "react";
import * as governor from "../../lib/networkGovernor";
import OfflineFallback from "./OfflineFallback";

const RELOAD_FLAG = "pl.chunk.reloaded";

const isChunkError = (err) => {
  const msg = String(err?.message || "");
  return (
    err?.name === "ChunkLoadError" ||
    /Loading (CSS )?chunk [^ ]+ failed|Failed to fetch dynamically imported module|Importing a module script failed/i.test(msg)
  );
};

export class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false, chunk: false };
  }

  static getDerivedStateFromError(err) {
    return { failed: true, chunk: isChunkError(err) };
  }

  componentDidCatch(err) {
    if (isChunkError(err) && !sessionStorage.getItem(RELOAD_FLAG)) {
      try {
        sessionStorage.setItem(RELOAD_FLAG, "1");
      } catch {
        /* private mode */
      }
      window.location.reload();
    }
  }

  componentDidMount() {
    // App sống khoẻ 15s → xoá guard để lần deploy SAU vẫn auto-reload được.
    this._t = setTimeout(() => {
      try {
        sessionStorage.removeItem(RELOAD_FLAG);
      } catch {
        /* noop */
      }
    }, 15_000);
    // Đợt Offline-3 — chunk fail lúc offline: mạng trở lại → tự thử lại lazy import.
    this._unsub = governor.subscribe((s) => {
      if (this.state.failed && this.state.chunk && s.state === "online") {
        this.setState({ failed: false, chunk: false });
      }
    });
  }

  componentWillUnmount() {
    clearTimeout(this._t);
    if (this._unsub) this._unsub();
  }

  render() {
    if (!this.state.failed) return this.props.children;
    if (this.state.chunk && governor.isOffline()) {
      return (
        <OfflineFallback
          onRetry={() => this.setState({ failed: false, chunk: false })}
        />
      );
    }
    return (
      <div
        data-testid="chunk-error-fallback"
        className="min-h-[60vh] flex flex-col items-center justify-center gap-3 text-center px-6"
      >
        <p className="text-sm font-semibold text-white/85">
          {this.state.chunk
            ? "Có bản cập nhật mới — cần tải lại trang."
            : "Có lỗi hiển thị. Vui lòng tải lại trang."}
        </p>
        <button
          type="button"
          data-testid="chunk-error-reload-btn"
          onClick={() => window.location.reload()}
          className="px-4 py-2 rounded-full bg-emerald-500/15 border border-emerald-400/40 text-emerald-300 text-sm font-bold hover:bg-emerald-500/25"
        >
          Tải lại
        </button>
      </div>
    );
  }
}

export default ChunkErrorBoundary;
